import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Input } from "~/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { cn } from "~/lib/cn";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  ensureCoachProfile,
  updateCoach,
  updateCoachProfile,
} from "~/lib/server/coach";
import { z } from "zod";
import { FormField } from "~/components/ui/form-field";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import {
  PristineInvalidAlert,
  usePristineInvalidAlert,
} from "~/components/pristine-invalid-alert";
import { ProfileEditHeader } from "../../../../../components/profile-edit-header";
import { Textarea } from "~/components/ui/textarea";
import { getAllCitiesGrouped } from "~/lib/functions/cities";
import { useDebounce } from "use-debounce";
import { findClosestMajorCity } from "~/lib/actions/get-closest-major-city";
import { createServerFn, useServerFn } from "@tanstack/react-start";
import { requireCoachAuth } from "~/lib/coach-middleware";

export const PersonalInfoSchema = z.object({
  id: z.string(),
  username: z.string(),
  fullName: z.string().min(2),
  email: z.string().email().min(5),
  phone: z.string().min(1),

  profileId: z.string().nullable().optional(),
  profile: z.object({
    backgroundInformation: z.string().max(2000).optional(),
    primaryCity: z.string().min(2),
    primaryCountry: z.string().min(2),
    closestMajorCity: z.string(),
  }),
});
type FormData = z.infer<typeof PersonalInfoSchema>;

export const getPersonalInfo = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const profile = await ensureCoachProfile(coach.id, coach.profileId);

    if (!profile) {
      throw new Error(`Profile not found for coach ${coach.id}`);
    }

    return {
      id: coach.id,
      username: coach.username,
      fullName: coach.fullName,
      email: coach.email,
      phone: coach.phone ?? "",
      profileId: profile.id,
      profile: {
        primaryCity: profile.primaryCity ?? "",
        primaryCountry: profile.primaryCountry ?? "",
        backgroundInformation: profile.backgroundInformation ?? "",
        closestMajorCity: profile.closestMajorCity ?? "",
      },
    };
  });

export const getCityOptions = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async () => {
    return await getAllCitiesGrouped();
  });

export const savePersonalInfo = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(PersonalInfoSchema)
  .handler(async ({ data }) => {
    await updateCoach(data.id, data);
    if (data.profileId) {
      await updateCoachProfile(data.profileId, data.profile);
    }

    return {
      status: "success",
      message: "Coach saved",
    };
  });

export const Route = createFileRoute(
  "/coach/_backend/edit-profile/personal-info/",
)({
  head: () =>
    createNoIndexMeta(
      "Personal Info – Dream Coach Match",
      "Edit your personal information and contact details",
    ),
  loader: async () => {
    const [coachData, cityOptions] = await Promise.all([
      getPersonalInfo(),
      getCityOptions(),
    ]);

    return { coachData, cityOptions };
  },
  component: PersonalInfoPage,
});

function PersonalInfoPage() {
  const queryClient = useQueryClient();
  const getPersonalInfoFn = useServerFn(getPersonalInfo);
  const { coachData, cityOptions } = Route.useLoaderData();

  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);

  const { register, handleSubmit, formState, watch, setValue, reset } =
    useForm<FormData>({
      resolver: zodResolver(PersonalInfoSchema),
      defaultValues: coachData,
    });

  const { errors, isDirty } = formState;

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => savePersonalInfo({ data }),
    onSuccess: async () => {
      toast.success("Personal information saved successfully");
      queryClient.invalidateQueries({ queryKey: ["coach"] });
      // Reset form with fresh data from the server to clear dirty state
      const freshData = await getPersonalInfoFn();
      reset(freshData);
    },
    onError: () => {
      toast.error("Failed to save personal information");
    },
  });

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  const { showExitAlert, onDiscard } = useUnsavedChangesAlert(() => isDirty);

  const pristineAlert = usePristineInvalidAlert(
    () => !isDirty && 0 < Object.keys(errors).length,
  );

  // Auto-fill closest major city when city or country changes
  const watchedCity = watch("profile.primaryCity");
  const watchedCountry = watch("profile.primaryCountry");
  const watchedClosestMajorCity = watch("profile.closestMajorCity");

  // Debounce the city and country values to prevent excessive API calls
  const [debouncedCity] = useDebounce(watchedCity, 1000);
  const [debouncedCountry] = useDebounce(watchedCountry, 1000);

  // Keep track of the last values we processed to avoid duplicate API calls
  const lastProcessedRef = useRef({ city: "", country: "" });

  const fillClosestMajorCity = useCallback(
    async (city: string, country: string) => {
      // Check if we've already processed these exact values
      if (
        !city ||
        !country ||
        (lastProcessedRef.current.city === city &&
          lastProcessedRef.current.country === country)
      ) {
        return;
      }

      setIsAutoFilling(true);
      lastProcessedRef.current = { city, country };

      try {
        const closestCity = await findClosestMajorCity({
          data: { city, country },
        });
        if (closestCity) {
          setValue("profile.closestMajorCity", closestCity);
        }
      } catch {
        // Silent fail for autofill, don't show error to user
      } finally {
        setIsAutoFilling(false);
      }
    },
    [setValue],
  );

  useEffect(() => {
    if (debouncedCity && debouncedCountry) {
      fillClosestMajorCity(debouncedCity, debouncedCountry);
    }
  }, [debouncedCity, debouncedCountry]);

  // Flatten city options for combobox (only when needed)
  const cityOptionsFlat = useMemo(() => {
    return cityOptions.flatMap((group) =>
      group.options.flatMap((country) =>
        country.options.map((city) => ({
          value: city.value || city.label,
          label: city.label,
          country: country.label,
          region: group.label,
          searchText: `${city.label} ${country.label}`.toLowerCase(),
        })),
      ),
    );
  }, [cityOptions]);

  // Get display label for current value
  const selectedCityLabel = useMemo(() => {
    if (!watchedClosestMajorCity) {
      return "";
    }
    const city = cityOptionsFlat.find(
      (city) => city.value === watchedClosestMajorCity,
    );
    return city?.label || watchedClosestMajorCity;
  }, [cityOptionsFlat, watchedClosestMajorCity]);

  return (
    <>
      <UnsavedChangesAlert show={showExitAlert} onDiscard={onDiscard} />
      <PristineInvalidAlert {...pristineAlert} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <ProfileEditHeader
          title="Personal Information"
          description="Basic information to identify you and communicate with you"
          form={formState}
          username={coachData.username}
        />

        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input {...register("id")} type="hidden" />
            <input {...register("username")} type="hidden" />
            <input {...register("profileId")} type="hidden" />

            <FormField
              label="Full Name"
              required
              helpText="Your professional name as you'd like it to appear"
              error={errors.fullName?.message}
            >
              <Input
                {...register("fullName")}
                error={errors.fullName?.message}
              />
            </FormField>

            <FormField
              label="Email"
              required
              helpText="Your primary email address"
              error={errors.email?.message}
              isPrivate
            >
              <Input
                {...register("email")}
                type="email"
                error={errors.email?.message}
              />
            </FormField>

            <FormField
              label="Phone"
              helpText="Your primary phone number"
              required
              error={errors.phone?.message}
              isPrivate
            >
              <Input {...register("phone")} error={errors.phone?.message} />
            </FormField>

            <FormField
              label="Primary City"
              required
              helpText="Your primary city of residence."
              error={errors.profile?.primaryCity?.message}
            >
              <Input
                {...register("profile.primaryCity")}
                error={errors.profile?.primaryCity?.message}
              />
            </FormField>

            <FormField
              label="Primary Country"
              required
              helpText="Your primary country of residence."
              error={errors.profile?.primaryCountry?.message}
            >
              <Input
                {...register("profile.primaryCountry")}
                error={errors.profile?.primaryCountry?.message}
              />
            </FormField>

            <FormField
              label="Closest Major City"
              helpText="The closest major city to your primary city of residence. This will be auto-filled when you enter your city and country above."
              error={errors.profile?.closestMajorCity?.message}
            >
              <Popover open={isComboboxOpen} onOpenChange={setIsComboboxOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={isComboboxOpen}
                    className="input flex items-center justify-between text-left"
                    disabled={isAutoFilling}
                  >
                    <span className="truncate">
                      {isAutoFilling
                        ? "Auto-filling..."
                        : selectedCityLabel || "Select a city..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder="Search cities..." />
                    <CommandList>
                      <CommandEmpty>No city found.</CommandEmpty>
                      <CommandGroup>
                        {cityOptionsFlat.slice(0, 100).map((city) => (
                          <CommandItem
                            key={city.value}
                            value={city.searchText}
                            onSelect={() => {
                              setValue("profile.closestMajorCity", city.value);
                              setIsComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                watchedClosestMajorCity === city.value
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{city.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {city.country}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <input {...register("profile.closestMajorCity")} type="hidden" />
            </FormField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Background Information</CardTitle>
            <CardDescription>
              Use this space to describe your background, expertise, tone of
              voice, style of working etc. to help our AI better understand you
              and your coaching style. This will be used when you use AI to
              generate values for certain fields.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              label="Background Information"
              error={errors.profile?.backgroundInformation?.message}
            >
              <Textarea
                {...register("profile.backgroundInformation")}
                error={errors.profile?.backgroundInformation?.message}
                maxLength={2000}
              />
            </FormField>
          </CardContent>
        </Card>
      </form>
    </>
  );
}
